import * as React from 'react';
import { PhaserToReactListeners, RemoveListener } from 'Phaser/EventBus2';
import type { Timestamp } from 'Types/ExerciseData';
import { timestampToSeconds } from 'Utils/VideoEventSchedule';

type NarrationRequest = { from: Timestamp; to: Timestamp };
type Playhead = Pick<HTMLVideoElement, 'paused' | 'currentTime'>;

/**
 * Whether the video is already playing through `[fromSeconds, toSeconds)`,
 * i.e. the requested narration is being heard right now. Skipping an objective
 * seeks the video to the next narration before the scene asks for it, and a
 * second seek there would restart the sentence.
 */
export function isPlayingWithin(
  video: Playhead | null,
  fromSeconds: number,
  toSeconds: number,
): boolean {
  if (video === null || video.paused) return false;
  return video.currentTime >= fromSeconds && video.currentTime < toSeconds;
}

/**
 * Plays the tutorial video's narration for an objective when the exercise
 * scene asks for it.
 *
 * The first objective of an exercise is narrated by the video playing into
 * it. For every later objective the video sits paused at the previous
 * objective's StartExercise cue, so the scene requests the segment that leads
 * into the next one. Playback then runs into that objective's own
 * StartExercise cue, which pauses the video and starts the exercise.
 */
export function useVideoNarration({
  getVideo,
  seekTo,
  play,
}: {
  getVideo: () => Playhead | null;
  /** Seek the video, and treat every event up to the target as handled. */
  seekTo: (seconds: number) => void;
  play: () => void;
}): void {
  React.useEffect(() => {
    const playNarration = ({ from, to }: NarrationRequest) => {
      const fromSeconds = timestampToSeconds(from);
      if (isPlayingWithin(getVideo(), fromSeconds, timestampToSeconds(to))) return;
      seekTo(fromSeconds);
      play();
    };
    PhaserToReactListeners.PlayNarration(playNarration);
    return () => RemoveListener('PlayNarration', playNarration);
  }, [getVideo, seekTo, play]);
}
